// eslint-disable-next-line flowtype/no-weak-types
// ===
import _ from "lodash"
import fp from "lodash/fp"
import { createSelector } from "reselect"
import moment from "moment"

import * as bookingSelectors from "mmb/scenes/AccountBooking/components/Booking/services/selector"
import {
  REASONS,
  STEP_DIRECTION,
  VIEWS,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/consts/refundConsts"
import { getNewStep } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/services/helpers"
import type { CalculatorData } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/components/BookingRefundCalculator/records/RefundOption"
import type { Reason } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/consts/refundConsts"
import phonePrefixes from "../../../../../../../../../data/countryPhonePrefixes.json" // eslint-disable-line import/no-restricted-paths

const invertedPhonePrefixes = _.invert(phonePrefixes)
const phoneToArr = fp.compose(
  fp.split(" "),
  fp.get("phone"),
)
const getPhonePrefix = fp.compose(
  fp.join(""),
  fp.tail,
  fp.first,
  phoneToArr,
)
const getPhoneNumber = fp.compose(
  fp.join(" "),
  fp.tail,
  phoneToArr,
)
const getEmail = fp.get("email")
const getPID = fp.get("pid")
const bookingRefundSelector = state => state.bookingRefund
export const bookingRefundDataSelector = createSelector(
  bookingRefundSelector,
  x => x.get("data"),
)
export const currentViewSelector = createSelector(
  bookingRefundSelector,
  x => x.get("currentView"),
)
export const apiCallStatusSelector = createSelector(
  bookingRefundSelector,
  x => x.get("apiCallFailed"),
)
export const isLoadingSelector = createSelector(
  bookingRefundSelector,
  x => x.get("loading"),
)
export const isCalculatorLoadedSelector = createSelector(
  bookingRefundSelector,
  x => x.get("calculatorLoaded"),
)
export const refundMethodsExpandedSelector = createSelector(
  bookingRefundSelector,
  x => x.get("refundMethodsExpanded"),
)
export const refundReasonSelector = (state: any): Reason =>
  state.bookingRefund.getIn(["data", "reason"])
export const affectedFlightsSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.getIn(["data", "affectedFlights"])
export const selectedAffectedFlightsSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.getIn(["data", "affectedFlights"]).filter(x => x === true)
export const affectedPassengersSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.getIn(["data", "affectedPassengers"])
export const selectedAffectedPassengersSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.getIn(["data", "affectedPassengers"]).filter(x => x === true)
export const isMedicalSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.get("medical")
export const selectedMethodSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.getIn(["data", "method"])

export const detailsSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.getIn(["data", "details"])

export const pathSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.get("path")
export const calculatorTypeSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.get("calculatorType")
export const calculatorDataSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.get("calculatorData")
export const assistedRefundSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.bookingRefund.get("assistedRefund")
export const initialContactFormDataSelector = (state: any) => {
  // eslint-disable-line flowtype/no-weak-types
  const { customer } = bookingSelectors.bookingDataSelector(state)
  const phoneCountry = invertedPhonePrefixes[getPhonePrefix(customer)]
  const phone = getPhoneNumber(customer)
  const email = getEmail(customer)
  const name = bookingSelectors
    .currentBookingSpecificPassengerSelector(state, getPID(customer))
    .getNameWithoutTitle()

  return {
    email,
    phoneCountry,
    phone,
    paypalEmail: email,
    paymentEmail: email,
    bankHolder: name,
  }
}
export const pendingRefundApplicationsSelector = createSelector(
  bookingRefundSelector,
  x => x.get("pendingApplications") || [],
)
export const affectedFlightsDataSelector = createSelector(
  [bookingSelectors.bookingDataSelector, selectedAffectedFlightsSelector],
  (booking, selectedAffectedFlights) => {
    const flights = booking.journey.getFlights()
    return flights.filter(x => selectedAffectedFlights.get(x.id))
  },
)
export const affectedPassengersDataSelector = createSelector(
  [bookingSelectors.bookingPassengersSelector, selectedAffectedPassengersSelector],
  (passengers, selectedAffectedPassengers) =>
    passengers.filter(x => selectedAffectedPassengers.get(x.id)),
)
export const isSelectedFlightsMissedSelector = createSelector(
  [bookingSelectors.currentBookingFlightsSelector, selectedAffectedFlightsSelector],
  (flights, selectedAffectedFlights) => {
    const isAnyFlightInFuture = flights.some(
      x =>
        selectedAffectedFlights.get(x.get("id")) &&
        moment.utc().isBefore(x.getIn(["departure", "when", "utc"])),
    )

    return !isAnyFlightInFuture
  },
)
export const pendingCalculatorSelector = createSelector(
  [bookingSelectors.bookingDataSelector, pendingRefundApplicationsSelector],
  (booking, pendingRefundApplications) => {
    const hasPendingAssisted = pendingRefundApplications.find(
      x => x.reason === REASONS.ASSISTED_REFUND,
    )

    return booking.refundOptions.requested || hasPendingAssisted?.created
  },
)
export const isCalculatorAvailableSelector = createSelector(
  [
    calculatorDataSelector,
    apiCallStatusSelector,
    bookingSelectors.bookingDataSelector,
    bookingSelectors.hasJourneyDepartedSelector,
    bookingSelectors.isBeforeAssistedRefundDeadlineSelector,
  ],
  (calculatorData, apiCallFailed, booking, flightHasDeparted, isBeforeAssistedRefundDeadline) => {
    const unableToRefundAutomatically =
      calculatorData && (!calculatorData.refundableOptions || !calculatorData.totalAmount)

    return (
      calculatorData &&
      !apiCallFailed &&
      !flightHasDeparted &&
      isBeforeAssistedRefundDeadline &&
      // Magic condition for zero amount refund cases
      (!unableToRefundAutomatically ||
        (calculatorData.totalAmount && calculatorData.totalAmount.amount_eur === 0))
    )
  },
)

export const passengerDocumentsSelector = createSelector(
  [bookingRefundSelector, bookingSelectors.bookingPassengersSelector],
  (refund, passengers) => {
    const documents = refund.getIn(["data", "documents"])

    return documents.map(document => ({
      ...document,
      name: passengers.find(passenger => passenger.id === document.pid).getFullName(),
    }))
  },
)
export const isFinalStepSelector = createSelector(
  pathSelector,
  path => {
    const nextStep = getNewStep(path, STEP_DIRECTION.NEXT)
    return nextStep.view === VIEWS.THANKYOU
  },
)
export const isZeroAmountRefundSelector = createSelector(
  calculatorDataSelector,
  ({ totalAmount, cancellationFee, refundableOptions }: CalculatorData) =>
    !refundableOptions || !totalAmount || !cancellationFee || totalAmount.amount_eur === 0,
)
